import axios from 'axios';
import { toast } from 'sonner';

// Resolve API base URL with a safe runtime fallback.
// - Preview/build has REACT_APP_BACKEND_URL hardcoded to the pod URL.
// - Production on custom domains (e.g. purplepepper.link) MUST talk to the
//   SAME origin so cookies and CORS just work. We detect that at runtime
//   by checking if the page hostname differs from the baked-in backend
//   hostname; if so we fall back to relative "/api".
function resolveApiBase() {
  const envBase = process.env.REACT_APP_BACKEND_URL;
  if (!envBase) return '/api';
  if (typeof window === 'undefined') return `${envBase}/api`;
  try {
    const envHost = new URL(envBase).hostname;
    const hereHost = window.location.hostname;
    // If we're on a different hostname than the baked-in backend AND the
    // current origin is https, prefer same-origin. This covers both custom
    // domains and any future Emergent subdomain remapping.
    if (envHost !== hereHost && window.location.protocol === 'https:') {
      return '/api';
    }
  } catch (_) {
    // If URL parsing fails fall through to the env value
  }
  return `${envBase}/api`;
}

const api = axios.create({
  baseURL: resolveApiBase(),
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Methods treated as writes — blocked while the browser is offline
const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);
const OFFLINE_MESSAGE = "You're offline — reconnect to save changes";

// Intercept requests: block writes when offline and show a friendly toast
api.interceptors.request.use((config) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    const method = (config.method || 'get').toLowerCase();
    if (WRITE_METHODS.has(method)) {
      toast.error(OFFLINE_MESSAGE);
      return Promise.reject({
        __offline: true,
        message: OFFLINE_MESSAGE,
        config,
      });
    }
  }
  return config;
});

// Intercept responses: if we got a 503 X-Offline from the SW or a network error, surface the same clear message
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error && error.__offline) return Promise.reject(error);
    if (!error?.response && (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error')) {
      const method = (error?.config?.method || 'get').toLowerCase();
      if (WRITE_METHODS.has(method)) toast.error(OFFLINE_MESSAGE);
    }
    return Promise.reject(error);
  }
);

export default api;
